import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, ChevronRight, Zap } from 'lucide-react';

const CORRECT_COLOR = '#22c55e';
const WRONG_COLOR = '#ef4444';

const InteractiveQuiz = ({ questions = [], onComplete, topicTitle }) => {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [fillAnswer, setFillAnswer] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);


  const q = questions[current];
  const progress = ((current) / questions.length) * 100;

  const checkAnswer = () => {
    if (!q) return;
    let correct = false;

    if (q.type === 'multiple_choice') {
      correct = selected === q.correct_index;
    } else if (q.type === 'true_false') {
      correct = selected === q.correct;
    } else if (q.type === 'fill_blank') {
      correct = fillAnswer.trim().toLowerCase() === (q.answer || '').toLowerCase();
    }

    setIsCorrect(correct);
    setShowFeedback(true);
    if (correct) setScore(s => s + 1);

  };

  const next = () => {
    setShowFeedback(false);
    setSelected(null);
    setFillAnswer('');
    if (current + 1 >= questions.length) {
      setFinished(true);
    } else {
      setCurrent(c => c + 1);
    }
  };

  const finalScore = Math.round((score / questions.length) * 100);

  if (finished) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center gap-6 py-12 text-center"
      >
        <div className={`w-28 h-28 rounded-full flex items-center justify-center text-4xl font-black text-white shadow-2xl`}
          style={{ background: finalScore >= 70 ? `linear-gradient(135deg, #22c55e, #16a34a)` : `linear-gradient(135deg, #f59e0b, #d97706)` }}>
          {finalScore}%
        </div>
        <div>
          <h2 className="text-2xl font-black text-gray-900 mb-1">
            {finalScore >= 70 ? '🎉 Excellent Work!' : '📚 Keep Practicing!'}
          </h2>
          <p className="text-gray-500 text-sm">
            You got {score} out of {questions.length} questions correct.
          </p>
        </div>
        <button
          onClick={() => onComplete && onComplete(finalScore)}
          className="flex items-center gap-2 bg-blue-600 text-white font-bold px-8 py-4 rounded-2xl hover:bg-blue-700 transition-all shadow-lg"
        >
          {finalScore >= 70 ? 'Continue to Next Stage' : 'Review & Retry'}
          <ChevronRight size={18} />
        </button>
      </motion.div>
    );
  }

  if (!q) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Question {current + 1} / {questions.length}</span>
          <span className="text-xs font-bold text-blue-500">{Math.round(progress)}% Complete</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -60 }}
          transition={{ duration: 0.25 }}
          className="flex-1 flex flex-col"
        >
          {/* Question card */}
          <div className="bg-white rounded-3xl border-2 border-gray-100 p-6 mb-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                <Zap size={16} />
              </div>
              <p className="font-bold text-gray-900 text-base leading-relaxed">{q.question}</p>
            </div>
          </div>

          {/* Answer inputs */}
          {q.type === 'multiple_choice' && (
            <div className="grid grid-cols-1 gap-3">
              {q.options.map((opt, i) => {
                let borderColor = 'border-gray-200';
                let bg = 'bg-white';
                let textColor = 'text-gray-800';
                if (showFeedback) {
                  if (i === q.correct_index) { borderColor = 'border-green-400'; bg = 'bg-green-50'; textColor = 'text-green-800'; }
                  else if (i === selected && i !== q.correct_index) { borderColor = 'border-red-400'; bg = 'bg-red-50'; textColor = 'text-red-800'; }
                }
                return (
                  <button
                    key={i}
                    disabled={showFeedback}
                    onClick={() => setSelected(i)}
                    className={`w-full text-left p-4 rounded-2xl border-2 font-semibold text-sm transition-all ${bg} ${textColor} ${borderColor} ${!showFeedback && selected === i ? 'border-blue-500 bg-blue-50 text-blue-800' : ''} ${!showFeedback ? 'hover:border-blue-300 hover:bg-blue-50/50' : ''}`}
                  >
                    <span className="inline-flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black border-2 ${!showFeedback && selected === i ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300'}`}>
                        {String.fromCharCode(65 + i)}
                      </span>
                      {opt}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {q.type === 'true_false' && (
            <div className="grid grid-cols-2 gap-4">
              {[true, false].map((val) => {
                let borderColor = 'border-gray-200';
                let bg = 'bg-white';
                let textColor = 'text-gray-800';
                if (showFeedback) {
                  if (val === q.correct) { borderColor = 'border-green-400'; bg = 'bg-green-50'; textColor = 'text-green-800'; }
                  else if (val === selected && val !== q.correct) { borderColor = 'border-red-400'; bg = 'bg-red-50'; textColor = 'text-red-800'; }
                }
                return (
                  <button
                    key={String(val)}
                    disabled={showFeedback}
                    onClick={() => setSelected(val)}
                    className={`p-6 rounded-2xl border-2 font-black text-lg transition-all ${bg} ${textColor} ${borderColor} ${!showFeedback && selected === val ? 'border-blue-500 bg-blue-50 text-blue-700' : ''} ${!showFeedback ? 'hover:border-blue-300' : ''}`}
                  >
                    {val ? '✅ True' : '❌ False'}
                  </button>
                );
              })}
            </div>
          )}

          {q.type === 'fill_blank' && (
            <div>
              <p className="text-xs font-bold text-gray-400 mb-3 uppercase tracking-widest">Fill in the blank:</p>
              <input
                type="text"
                disabled={showFeedback}
                value={fillAnswer}
                onChange={e => setFillAnswer(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !showFeedback && checkAnswer()}
                placeholder="Type your answer..."
                className={`w-full p-4 rounded-2xl border-2 font-semibold text-gray-800 outline-none transition-all ${showFeedback ? (isCorrect ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50') : 'border-gray-200 focus:border-blue-400 bg-white'}`}
              />
            </div>
          )}

          {/* Feedback */}
          <AnimatePresence>
            {showFeedback && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`mt-4 p-4 rounded-2xl flex items-start gap-3 ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}
              >
                {isCorrect
                  ? <CheckCircle size={20} className="text-green-600 shrink-0 mt-0.5" />
                  : <XCircle size={20} className="text-red-600 shrink-0 mt-0.5" />
                }
                <div>
                  <p className={`font-black text-sm ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                    {isCorrect ? 'Correct!' : 'Not quite.'}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">{q.explanation}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>

      {/* Action button */}
      <div className="mt-6">
        {!showFeedback ? (
          <button
            onClick={checkAnswer}
            disabled={selected === null && !fillAnswer.trim()}
            className="w-full bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-black py-4 rounded-2xl transition-all hover:bg-blue-700 shadow-lg"
          >
            Check Answer
          </button>
        ) : (
          <button
            onClick={next}
            className="w-full font-black py-4 rounded-2xl transition-all text-white shadow-lg"
            style={{ background: isCorrect ? `linear-gradient(135deg, ${CORRECT_COLOR}, #16a34a)` : `linear-gradient(135deg, ${WRONG_COLOR}, #dc2626)` }}
          >
            {current + 1 >= questions.length ? 'See Results' : 'Next Question →'}
          </button>
        )}
      </div>
    </div>
  );
};

export default InteractiveQuiz;
